import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchTournamentAdmins,
  fetchTournamentById,
  updateTournamentAdmins,
} from '../../../services/tournamentService';
import { buildManagementScope, buildMatchManagerFormatsPath } from '../../../utils/managementNavigation';
import { normalizeId, resolveMatchManagerId } from '../../../utils/ids';
import { hasIndividualAccess } from '../../../utils/userRoles';

export const buildIndividualTournamentPath = (tournamentId, destination) => {
  const normalizedTournamentId = normalizeId(tournamentId);
  if (!normalizedTournamentId || !destination) return '';

  return `/individual-tournament/${normalizedTournamentId}/${destination}`;
};

export const MANAGEMENT_CARDS = [
  {
    key: 'registrationManager',
    label: 'Player Management',
    description: 'Add and manage tournament players.',
    href: ({ tournamentId }) => buildIndividualTournamentPath(tournamentId, 'players'),
  },
  {
    key: 'auctionManager',
    label: 'Auction Management',
    description: 'Setup and run tournament auctions.',
    href: ({ tournamentId }) => buildIndividualTournamentPath(tournamentId, 'auction'),
  },
  {
    key: 'matchManager',
    label: 'Match Management',
    description: 'Create and manage matches.',
    href: ({ tournamentId, managerId }) => buildMatchManagerFormatsPath(
      buildManagementScope({ individualTournamentId: tournamentId }),
      managerId,
      tournamentId,
    ),
  },
];

const getUserLabel = (user) => user?.name || user?.email || user?.mobile || 'Not assigned';


const LoadingState = ({ message }) => (
  <PageShell>
    <div className="container py-4" style={{ maxWidth: 980 }}>
      <div className="alert alert-info mb-0">{message}</div>
    </div>
  </PageShell>
);

export default function IndividualTournamentAdminPage() {
  const navigate = useNavigate();
  const { id, tournamentId: routeTournamentId } = useParams();
  const tournamentId = routeTournamentId || id;
  const { token, user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [managerCandidates, setManagerCandidates] = useState([]);
  const [managers, setManagers] = useState({ registrationManager: '', auctionManager: '', matchManager: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  const [loading, setLoading] = useState(true);
  const hasAccess = useMemo(() => hasIndividualAccess(user), [user]);
  const currentUserId = normalizeId(user);
  const isTournamentOwnerOrCreator = useMemo(() => {
    if (!tournament) return false;
    const ownerType = String(tournament?.ownerType || tournament?.entityType || '').toLowerCase();
    const ownerUserId = normalizeId(tournament?.ownerUser);
    const createdById = normalizeId(tournament?.createdBy);
    return ownerType === 'individual' && (ownerUserId === currentUserId || createdById === currentUserId);
  }, [tournament, currentUserId]);
  const isAssignedManager = useMemo(() => {
    if (!tournament) return false;
    return ['registrationManager', 'auctionManager', 'matchManager']
      .some((key) => normalizeId(tournament?.[key]) === currentUserId);
  }, [tournament, currentUserId]);


  useEffect(() => {
    if (!token || !tournamentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchTournamentById(tournamentId, token)
      .then(async (tournamentResponse) => {
        const loadedTournament = tournamentResponse?.tournament || tournamentResponse || null;
        setTournament(loadedTournament);
        setManagers({
          registrationManager: normalizeId(loadedTournament?.registrationManager),
          auctionManager: normalizeId(loadedTournament?.auctionManager),
          matchManager: normalizeId(loadedTournament?.matchManager),
        });
        let initialUsers = [];
        try {
          const adminsResponse = await fetchTournamentAdmins(token, null, { includeUsers: true });
          initialUsers = adminsResponse?.users || [];
        } catch (_error) {
          // Individual tournaments can be managed without org/community/club admin listing context.
          initialUsers = [];
        }
        const merged = [...initialUsers];
        [loadedTournament?.ownerUser, loadedTournament?.createdBy].forEach((participant) => {
          const participantId = normalizeId(participant);
          if (!participantId) return;
          if (!merged.some((candidate) => normalizeId(candidate) === participantId)) {
            merged.push(participant);
          }
        });
        [loadedTournament?.registrationManager, loadedTournament?.auctionManager, loadedTournament?.matchManager].forEach((manager) => {
          const managerId = normalizeId(manager);
          if (!managerId) return;
          if (!merged.some((candidate) => normalizeId(candidate) === managerId)) {
            merged.push(manager);
          }
        });
        setManagerCandidates(merged);
      })
      .catch((err) => setError(err.message || 'Unable to load tournament admin workspace.'))
      .finally(() => {
        setLoading(false);
      });
  }, [token, tournamentId]);

  if (!token) return <Navigate to="/login" replace />;

  const allowed = isTournamentOwnerOrCreator || isAssignedManager || hasAccess;
  if (loading) {
    return <LoadingState message="Loading tournament management..." />;
  }

  if (!loading && tournament && !allowed) {
    return <Navigate to="/dashboard/individual/tournaments" replace />;
  }

  const onAssignManager = async (roleKey) => {
    if (!tournamentId || !tournament) return;
    const selectedManagerId = managers[roleKey] || '';
    const currentManagerId = normalizeId(tournament[roleKey]);
    const roleLabel = roleKey === 'registrationManager' ? 'player management' : roleKey === 'auctionManager' ? 'auction management' : 'match management';


    setSaving(roleKey);
    setStatus('');
    setError('');
    try {
      const payload = {
        registrationManagerId: roleKey === 'registrationManager' ? selectedManagerId : normalizeId(tournament.registrationManager),
        auctionManagerId: roleKey === 'auctionManager' ? selectedManagerId : normalizeId(tournament.auctionManager),
        matchManagerId: roleKey === 'matchManager' ? selectedManagerId : normalizeId(tournament.matchManager),
      };
      const response = await updateTournamentAdmins(tournamentId, tournament.admins || [], token, payload);
      const updatedTournament = response?.tournament || tournament;
      setTournament(updatedTournament);
      const updatedId = normalizeId(updatedTournament[roleKey]);
      if (updatedId) {
        setStatus(`${roleLabel} manager assigned.`);
      } else {
        setStatus(`${roleLabel} manager removed.`);
      }
    } catch (assignError) {
      setError(assignError.message || 'Unable to update managers.');
    } finally {
      setSaving('');
    }
  };

  const onOpenManagementCard = (card) => {
    const resolvedTournamentId =
      normalizeId(tournament) ||
      normalizeId(tournamentId) ||
      normalizeId(routeTournamentId) ||
      normalizeId(id);

    if (!resolvedTournamentId) {
      setError('Unable to open this workspace because tournament ID is missing.');
      return;
    }

    const managerId = normalizeId(user?._id || user?.id);
    const assignedManagerId = normalizeId(tournament?.matchManager);
    const targetUrl = typeof card.href === 'function'
      ? card.href({
        tournamentId: resolvedTournamentId,
        managerId: card.key === 'matchManager' ? (assignedManagerId || managerId) : managerId,
      })
      : card.href;
    console.log('TOURNAMENT_FOR_NAVIGATION', tournament);
    console.log('TOURNAMENT_ID_FOR_NAVIGATION', tournament?._id || tournament?.id);
    navigate(targetUrl);
  };

  return (
    <PageShell>
      <div className="container py-4" style={{ maxWidth: 980 }}>
        <div className="card border-0 overflow-hidden shadow-sm mb-4">
          {tournament?.tournamentBanner ? <img src={tournament.tournamentBanner} alt="Tournament banner" style={{ width: '100%', height: 180, objectFit: 'cover' }} /> : null}
          <div className="p-3 d-flex align-items-center gap-3">
            {tournament?.tournamentLogo ? <img src={tournament.tournamentLogo} alt="Tournament logo" style={{ height: 64, width: 64, borderRadius: 999, objectFit: 'cover' }} /> : null}
            <div>
              <h1 className="h4 mb-1">{tournament?.name || 'Tournament admin workspace'}</h1>
              <span className="badge text-bg-primary">Individual Tournament</span>
            </div>
          </div>
        </div>

        {status ? <div className="alert alert-success">{status}</div> : null}
        {error ? <div className="alert alert-warning">{error}</div> : null}

        <div className="row g-3">
          {MANAGEMENT_CARDS.map((card) => {
            const assignedManager = managerCandidates.find((candidate) => normalizeId(candidate) === normalizeId(tournament?.[card.key])) || tournament?.[card.key];
            const resolvedCardTournamentId = normalizeId(tournament) || normalizeId(tournamentId);
            const currentManagerId = normalizeId(user);
            const assignedMatchManagerId = resolveMatchManagerId({ tournament, currentUser: user, user });
            const cardHref = typeof card.href === 'function'
              ? card.href({
                tournamentId: resolvedCardTournamentId,
                managerId: card.key === 'matchManager' ? (assignedMatchManagerId || currentManagerId) : currentManagerId,
              })
              : card.href;
            return (
              <div className="col-md-4" key={card.key}>
                <div className="card h-100">
                  <div className="card-body d-flex flex-column gap-2">
                    <h2 className="h6 mb-0">{card.label}</h2>
                    <p className="text-muted mb-0">{card.description}</p>
                    <small className="text-secondary">Assigned: {getUserLabel(assignedManager)}</small>
                    <select
                      className="form-select form-select-sm"
                      value={managers[card.key] || ''}
                      onChange={(event) => setManagers((prev) => ({ ...prev, [card.key]: event.target.value }))}
                    >
                      <option value="">Unassigned</option>
                      {managerCandidates.map((candidate) => (
                        <option value={normalizeId(candidate)} key={normalizeId(candidate)}>{getUserLabel(candidate)}</option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onAssignManager(card.key)} disabled={saving === card.key}>
                      {saving === card.key ? 'Saving...' : 'Invite Manager'}
                    </button>
                    {card.key === 'registrationManager' ? (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => onOpenManagementCard(card)}>
                        Open
                      </button>
                    ) : (
                      <Link to={cardHref} className="btn btn-primary btn-sm">Open</Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
